import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { PrimaryButton } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import { clearClaimDraft } from '../claims/claimDraftStorage'
import { formatBasicExcessDisplay } from '../claims/windscreenExcessUtils'
import { CLAIM_SUBMITTED_HEADING, ClaimSubmittedSummaryCard } from './ClaimSubmittedConfirmation'

function buildClaimsHubSearch(policyRef: string | null, typeInQuery: string | null): string {
  const p = new URLSearchParams()
  if (policyRef) p.set('policyRef', policyRef)
  if (typeInQuery) p.set('type', typeInQuery)
  const q = p.toString()
  return q ? `?${q}` : ''
}

/**
 * Post-submit confirmation for single-vehicle collision FNOL.
 */
export default function ClaimOtherSingleVehicleSubmittedStep() {
  const navigate = useNavigate()
  const location = useLocation()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const navState = location.state as {
    policy?: PolicyLink
    preserveScroll?: boolean
    singleVehicleClaimSubmitted?: boolean
    claimNumber?: string | null
  } | null
  const policy = navState?.policy
  const claimNumber =
    typeof navState?.claimNumber === 'string' && navState.claimNumber.trim()
      ? navState.claimNumber.trim()
      : null

  const hubSearch = buildClaimsHubSearch(policyRef, typeInQuery)
  const claimsHubPath = `/claims${hubSearch}`

  const basicExcess = formatBasicExcessDisplay(policy)

  useEffect(() => {
    if (!navState?.preserveScroll) window.scrollTo(0, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsFlowMain>
        <h1 className="m-0 font-display text-display-xs font-semibold leading-display-xs text-text-primary-900 md:text-display-sm md:leading-display-sm">
          {CLAIM_SUBMITTED_HEADING}
        </h1>

        <ClaimSubmittedSummaryCard branch="other" claimNumber={claimNumber} basicExcess={basicExcess} />

        <div className="w-full max-w-[768px] pb-4 md:pb-0">
          <PrimaryButton
            type="button"
            size="lg"
            fullWidth
            className="sm:w-auto sm:min-w-[200px]"
            onClick={() => {
              clearClaimDraft()
              navigate(claimsHubPath, { state: policy ? { policy } : undefined })
            }}
          >
            Back to claims hub
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
